/**
 * Marketing → concierge-iframe language sync.
 *
 * The guest SPA is embedded in the marketing site's concierge page via an
 * iframe pointing at `/myhotelspace`. The marketing language switcher keeps
 * the iframe in sync two ways:
 *
 *   1. Initial load — marketing appends `?lng=<code>` to the iframe `src`.
 *      Honored at boot by `i18n/index.ts` (`resolveInitialLang`); no code
 *      here is involved for the cold-start case.
 *
 *   2. Runtime — while the iframe is already mounted, marketing posts
 *      `{ type: 'cordiant:set-language', lang: '<code>' }` to the iframe's
 *      contentWindow. `installLanguageBridge` applies it via i18next.
 *
 * On boot the iframe posts `{ type: 'cordiant:ready' }` to the parent
 * (`notifyParentLanguageReady`) so marketing can (re)send the current
 * language even if its runtime message would otherwise have raced the
 * iframe mount, and again after auth so a language chosen while logged out
 * gets persisted once the guest is known.
 *
 * Origin is locked to the known marketing origins (`PARENT_TARGET_ORIGINS`,
 * shared with the auth bridge). When the guest is authenticated, an applied
 * language is also persisted via `hotelService` so it survives future loads.
 */
import i18n, { normalizeLocale, markLanguageSetByHost } from '../i18n';
import { PARENT_TARGET_ORIGINS } from './parentAuth';
import { hotelService } from '../services/hotel.service';

/** Marketing → iframe: apply this language now. */
export const HOST_SET_LANGUAGE_MESSAGE = 'cordiant:set-language';
/** iframe → marketing: SPA is mounted and listening; (re)send current language. */
export const IFRAME_READY_MESSAGE = 'cordiant:ready';

interface LanguageBridgeContext {
  hotelId?: string;
  guestEmail?: string;
}

/**
 * Post a one-time readiness handshake to the marketing parent so it can push
 * the current language. No-op when the SPA is at top level (not iframed).
 * Emits per-origin because `postMessage` takes a single `targetOrigin`; the
 * browser rejects the ones that don't match the real parent origin.
 */
export function notifyParentLanguageReady(): void {
  if (typeof window === 'undefined') return;
  if (window.parent === window) return; // not iframed
  const payload = { type: IFRAME_READY_MESSAGE, ts: Date.now() };
  for (const origin of PARENT_TARGET_ORIGINS) {
    try {
      window.parent.postMessage(payload, origin);
    } catch {
      // Origin mismatch is expected for every origin except the real parent.
    }
  }
}

/**
 * Install the `cordiant:set-language` listener. Returns a teardown fn.
 *
 * `getContext` is invoked on each message so the persist step always sees the
 * latest hotelId / guestEmail (callers pass a ref reader, not a snapshot).
 */
export function installLanguageBridge(
  getContext: () => LanguageBridgeContext,
): () => void {
  if (typeof window === 'undefined') return () => {};

  const onMessage = (event: MessageEvent) => {
    // Security: only trust the known marketing origins.
    if (!PARENT_TARGET_ORIGINS.includes(event.origin)) return;

    const data = event.data;
    if (!data || typeof data !== 'object' || data.type !== HOST_SET_LANGUAGE_MESSAGE) {
      return;
    }

    const lang = normalizeLocale(data.lang);
    if (!lang) return; // unsupported / empty — ignore silently

    // The host explicitly chose a language for this session; from now on the
    // backend's persisted preference must not override it (see UserContext).
    markLanguageSetByHost();

    if (lang !== i18n.language) {
      void i18n.changeLanguage(lang);
    }

    // Persist for authenticated guests so the choice survives future loads.
    // Logged-out guests have no email — marketing stays the source of truth
    // (it re-sends on the ready handshake and after auth-success).
    const { hotelId, guestEmail } = getContext();
    if (hotelId && guestEmail) {
      hotelService.setUserPreferredLanguage(hotelId, guestEmail, lang).catch((err) => {
        console.error('[languageBridge] Failed to persist preferred language', err);
      });
    }
  };

  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}
